// The people on an outlet's till, by name (slice 82, ADR 0082).
//
// The list is what the till last said: the cloud holds no PIN and cannot sign
// anyone in. The one action is asking for someone to be taken off the till; the
// till does it at its next round and answers, so "Waiting for the till",
// "The till kept them" and "Removed by you" are the till's words, not a guess.

import { useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../api/client.js';
import type { PosStaffPerson } from '../../api/pos-types.js';
import { Chip, QueryState, actionErrorText, type TabProps } from './common.js';
import { formatDateTime } from './format.js';

const ROLE_TEXT = {
  waiter: 'Waiter (tablet only)',
  cashier: 'Cashier',
  manager: 'Manager',
} as const;

const REFUSED_TEXT = {
  last_manager:
    'The till kept them: it needs at least one manager. Add another manager at the till first, then ask again.',
  unknown_staff: 'The till does not know this person any more.',
} as const;

export interface StaffTabProps extends TabProps {
  /** pos:staff_manage */
  readonly canRemove: boolean;
}

function statusCell(p: PosStaffPerson): JSX.Element {
  if (p.status === 'removed') {
    return (
      <Chip>{p.removedBy === 'owner' ? 'Removed from the dashboard' : 'Removed at the till'}</Chip>
    );
  }
  if (p.removal.state === 'waiting') {
    return <Chip tone="warn">Waiting for the till to remove them</Chip>;
  }
  if (p.removal.state === 'refused') {
    return (
      <>
        <Chip tone="bad">Not removed</Chip>
        <div className="muted">
          {p.removal.refusedCode ? REFUSED_TEXT[p.removal.refusedCode] : 'The till did not do it.'}
        </div>
      </>
    );
  }
  return <Chip tone="ok">On the till</Chip>;
}

export function StaffTab({ orgId, outletId, canRemove }: StaffTabProps): JSX.Element {
  const qc = useQueryClient();
  const key = ['pos-staff', orgId, outletId] as const;
  const [confirming, setConfirming] = useState<PosStaffPerson | null>(null);

  // The till answers within a minute or so; look again now and then while anyone is waiting.
  const staff = useQuery({
    queryKey: key,
    queryFn: () =>
      apiFetch<{ staff: PosStaffPerson[] }>(
        `/organizations/${orgId}/outlets/${outletId}/pos/staff`,
      ),
    refetchInterval: (q) =>
      q.state.data?.staff.some((p) => p.removal.state === 'waiting') ? 10_000 : false,
  });
  const remove = useMutation({
    mutationFn: (p: PosStaffPerson) =>
      apiFetch(`/organizations/${orgId}/outlets/${outletId}/pos/staff/${p.staffId}/remove`, {
        method: 'POST',
        body: {},
      }),
    onSuccess: () => setConfirming(null),
    onSettled: () => void qc.invalidateQueries({ queryKey: key }),
  });
  const error = actionErrorText(remove.error);

  return (
    <section aria-labelledby="staff-heading">
      <h2 id="staff-heading">Staff</h2>
      <p className="muted">
        The people on this outlet’s till, as the till last reported them. People are added, given a
        PIN and given a role at the till. From here you can ask for someone to be taken off it.
      </p>
      <QueryState loading={staff.isLoading} error={staff.error} />
      {error ? (
        <p className="error" role="alert">
          {error}
        </p>
      ) : null}
      {staff.data && staff.data.staff.length === 0 ? (
        <p className="muted">
          The till has not reported anyone yet. People appear here once the till is online.
        </p>
      ) : null}
      {staff.data && staff.data.staff.length > 0 ? (
        <div className="table-wrap">
          <table className="pos-table">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Role</th>
                <th scope="col">Status</th>
                <th scope="col">First seen</th>
                {canRemove ? <th scope="col">Actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {staff.data.staff.map((p) => (
                <tr key={p.staffId}>
                  <th scope="row">{p.name}</th>
                  <td>{ROLE_TEXT[p.role]}</td>
                  <td>{statusCell(p)}</td>
                  <td>{formatDateTime(p.firstSeenAt)}</td>
                  {canRemove ? (
                    <td>
                      {p.status === 'active' && p.removal.state !== 'waiting' ? (
                        <button
                          type="button"
                          className="secondary"
                          disabled={remove.isPending}
                          aria-label={`Take ${p.name} off the till`}
                          onClick={() => setConfirming(p)}
                        >
                          {p.removal.state === 'refused' ? 'Ask again' : 'Take off the till'}
                        </button>
                      ) : null}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {confirming ? (
        <div className="card confirm" role="alertdialog" aria-label="Take this person off the till">
          <p>
            <strong>Take {confirming.name} off the till?</strong>
          </p>
          <p className="muted">
            They can no longer sign in at the till or on a tablet, once the till is online and has
            picked this up. Their past bills keep their name. The till will not remove its last
            manager. This cannot be undone from here.
          </p>
          <div className="row">
            <button
              type="button"
              disabled={remove.isPending}
              onClick={() => remove.mutate(confirming)}
            >
              Yes, take them off
            </button>
            <button type="button" className="secondary" onClick={() => setConfirming(null)}>
              Keep them
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
